import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Button, TextField, Paper, CircularProgress, Alert,
    InputAdornment, Grid, Chip
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { pricingAPI, carTypesAPI } from '../../services/apiService';

const FormulaSettingsManager = () => {
    const [settings, setSettings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [dirty, setDirty] = useState(false);
    const [carTypeLabels, setCarTypeLabels] = useState({});

    const fetchSettings = useCallback(async () => {
        try {
            setLoading(true);
            const [formulaData, carTypeData] = await Promise.all([
                pricingAPI.getFormulaSettings(),
                carTypesAPI.getAll(),
            ]);
            setSettings(formulaData);
            const labels = {};
            (carTypeData || []).forEach(ct => { labels[ct.id] = ct.name; });
            setCarTypeLabels(labels);
            setError(null);
            setDirty(false);
        } catch (err) {
            setError('Failed to load formula settings: ' + (err.response?.data?.error || err.message));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchSettings(); }, [fetchSettings]);

    const handleRateChange = (id, field, value) => {
        setSettings(prev => prev.map(s => s.id === id ? { ...s, [field]: parseFloat(value) || 0 } : s));
        setDirty(true);
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            const updated = await pricingAPI.updateFormulaSettings(settings);
            setSettings(updated);
            setDirty(false);
            setSuccess('Formula settings saved');
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            setError('Save failed: ' + (err.response?.data?.error || err.message));
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>;
    }

    return (
        <Box>
            {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>{success}</Alert>}

            {/* ── Header ── */}
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Box>
                    <Typography variant="h6">Per-Mile Rates by Car Type</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Formula: <strong>rate × distance + tolls</strong>. Round trip = one-way × multiplier. Min fare applies.
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={saving || !dirty}
                    sx={{ flexShrink: 0, ml: 2 }}
                >
                    {saving ? 'Saving...' : 'Save Rates'}
                </Button>
            </Box>

            {/* ── Car type cards ── */}
            <Grid container spacing={2}>
                {settings.map((s) => (
                    <Grid key={s.id} size={{ xs: 12, sm: 6, md: 4 }}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                                <Typography variant="subtitle2" fontWeight="bold">
                                    {carTypeLabels[s.carType] || `Type ${s.carType}`}
                                </Typography>
                                <Chip
                                    label={`50 mi: $${(s.perMileRate * 50).toFixed(2)}`}
                                    size="small"
                                    variant="outlined"
                                    color="default"
                                />
                            </Box>
                            <Box display="flex" flexDirection="column" gap={1.5}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Per Mile Rate"
                                    type="number"
                                    value={s.perMileRate}
                                    onChange={(e) => handleRateChange(s.id, 'perMileRate', e.target.value)}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                        inputProps: { step: 0.1, min: 0 }
                                    }}
                                />
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Min Per Hour Rate"
                                    type="number"
                                    value={s.minPerHourRate ?? 0}
                                    onChange={(e) => handleRateChange(s.id, 'minPerHourRate', e.target.value)}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                        inputProps: { step: 1, min: 0 }
                                    }}
                                    helperText="0 = use global default"
                                />
                            </Box>
                        </Paper>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
};

export default FormulaSettingsManager;
